/**
 * Client portal form/assessment submission via Medplum bots.
 *
 * Patients must not write QuestionnaireResponse resources directly — the bots
 * validate the open Task, apply compartment meta, and create or update the QR.
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Extension, Identifier, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { CLIENT_SIGNATURE_EXT_URL } from '../pages/appointments/fhirNoteMapping';
import { isAnyAssessmentTask } from './clientFacingTasks';

export const FORM_SUBMISSION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'form-submission',
} as const;

export const ASSESSMENT_SUBMISSION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'assessment-submission',
} as const;

export type PortalSubmissionBotIdentifier =
  | typeof FORM_SUBMISSION_BOT_IDENTIFIER
  | typeof ASSESSMENT_SUBMISSION_BOT_IDENTIFIER;

export interface PortalFormSubmissionResult {
  questionnaireResponseId: string;
  taskId: string;
  patientId?: string;
  zoomResyncFailures?: ZoomResyncFailure[];
}

export type ZoomResyncFailure = {
  appointmentId: string;
  meetingId?: string;
  operation: 'patch' | 'verify' | 'live-stop' | 'delete';
  reason: string;
  attempts: number;
};

export type SessionRecordingConsentMutationResult = {
  consentId: string;
  questionnaireResponseId?: string;
  zoomResyncFailures?: ZoomResyncFailure[];
};

function resolveBotExecuteTarget(
  botIdEnvKey: keyof ImportMetaEnv,
  systemEnvKey: keyof ImportMetaEnv,
  valueEnvKey: keyof ImportMetaEnv,
  fallback: Identifier,
): string | Identifier {
  const id = import.meta.env[botIdEnvKey]?.trim();
  if (id) {
    return id;
  }
  const system = import.meta.env[systemEnvKey]?.trim() ?? fallback.system;
  const value = import.meta.env[valueEnvKey]?.trim() ?? fallback.value;
  return { system, value };
}

export function getFormSubmissionBotExecuteTarget(): string | Identifier {
  return resolveBotExecuteTarget(
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_ID',
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_IDENTIFIER_SYSTEM',
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_IDENTIFIER_VALUE',
    FORM_SUBMISSION_BOT_IDENTIFIER,
  );
}

export function getAssessmentSubmissionBotExecuteTarget(): string | Identifier {
  return resolveBotExecuteTarget(
    'VITE_MEDPLUM_ASSESSMENT_SUBMISSION_BOT_ID',
    'VITE_MEDPLUM_ASSESSMENT_SUBMISSION_BOT_IDENTIFIER_SYSTEM',
    'VITE_MEDPLUM_ASSESSMENT_SUBMISSION_BOT_IDENTIFIER_VALUE',
    ASSESSMENT_SUBMISSION_BOT_IDENTIFIER,
  );
}

/**
 * Pick the bot from Task shape — not UI labels.
 * QR-focus tasks always use form-submission (provider-started chart documents).
 */
export function resolveClientPortalSubmissionBot(task: Task): PortalSubmissionBotIdentifier {
  const focus = task.focus?.reference ?? '';
  if (focus.startsWith('QuestionnaireResponse/')) {
    return FORM_SUBMISSION_BOT_IDENTIFIER;
  }
  if (isAnyAssessmentTask(task)) {
    return ASSESSMENT_SUBMISSION_BOT_IDENTIFIER;
  }
  return FORM_SUBMISSION_BOT_IDENTIFIER;
}

/**
 * Minimal QR payload for bot submission — answers (+ optional client signature).
 * Status, subject, meta, and chart workflow extensions are applied server-side.
 */
export function buildPortalFormSubmissionDraft(
  qr: QuestionnaireResponse,
): Partial<QuestionnaireResponse> {
  const extensions: Extension[] = [];
  for (const ext of qr.extension ?? []) {
    if (ext.url === CLIENT_SIGNATURE_EXT_URL) {
      extensions.push(ext);
    }
  }

  return {
    resourceType: 'QuestionnaireResponse',
    item: qr.item,
    ...(extensions.length > 0 ? { extension: extensions } : {}),
  };
}

export async function submitClientPortalFormTask(
  medplum: MedplumClient,
  params: {
    task: Task;
    questionnaireResponse: QuestionnaireResponse;
  },
): Promise<PortalFormSubmissionResult> {
  const taskId = params.task.id;
  if (!taskId) {
    throw new Error('Task is missing an id.');
  }

  const bot = resolveClientPortalSubmissionBot(params.task);
  const target = bot === ASSESSMENT_SUBMISSION_BOT_IDENTIFIER
    ? getAssessmentSubmissionBotExecuteTarget()
    : getFormSubmissionBotExecuteTarget();

  return medplum.executeBot(
    target,
    {
      taskId,
      questionnaireResponse: buildPortalFormSubmissionDraft(params.questionnaireResponse),
    },
    ContentType.JSON,
  ) as Promise<PortalFormSubmissionResult>;
}

export async function revokeSessionRecordingConsent(
  medplum: MedplumClient,
  params: { targetPatientId: string },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      action: 'session-recording-revoke',
      targetPatientId: params.targetPatientId,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function revokeAiToolsConsent(
  medplum: MedplumClient,
  params: { targetPatientId: string },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      action: 'ai-tools-consent-revoke',
      targetPatientId: params.targetPatientId,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function optInAiToolsConsent(
  medplum: MedplumClient,
  params: {
    targetPatientId: string;
    signature: { name?: string; signatureData: string; signedAt?: string };
  },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      action: 'ai-tools-consent-opt-in',
      targetPatientId: params.targetPatientId,
      signature: params.signature,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function ensureAiToolsConsent(
  medplum: MedplumClient,
  params: { targetPatientId: string },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      action: 'ai-tools-consent-ensure',
      targetPatientId: params.targetPatientId,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function optInSessionRecordingConsent(
  medplum: MedplumClient,
  params: {
    targetPatientId: string;
    signature: { name?: string; signatureData: string; signedAt?: string };
  },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      action: 'session-recording-opt-in',
      targetPatientId: params.targetPatientId,
      signature: params.signature,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

/** Non-blocking copy when Zoom meeting settings could not be fully synced after consent change. */
export function sessionRecordingZoomResyncWarningMessage(
  failures: ZoomResyncFailure[] | undefined,
): string | undefined {
  if (!failures?.length) return undefined;
  return 'Your consent was saved, but meeting recording settings are still syncing. Support has been notified.';
}
